import { canPlayNativeHls, loadWithHls } from "./hls-loader.js";
import type { ControllerContext } from "./index.js";
import { emitError } from "./index.js";

/**
 * Refresh the signed URL token without interrupting playback.
 * Preserves current playback position and quality/caption settings.
 */
export async function refreshToken(ctx: ControllerContext): Promise<void> {
  if (!ctx.video || !ctx.titleId || !ctx.tokenRefreshHandler || ctx.isRefreshing) {
    return;
  }

  ctx.isRefreshing = true;

  try {
    // Fetch new signed URL
    const freshSource = await ctx.tokenRefreshHandler(ctx.titleId);

    // Store current playback state
    const currentTime = ctx.video.currentTime || 0;
    const isPaused = ctx.video.paused;

    // Reload source with fresh token
    if (ctx.hls) {
      try {
        ctx.hls.destroy();
      } catch {
        // ignore
      }
      ctx.hls = null;
    }

    const canNative = canPlayNativeHls(ctx.video);
    if (canNative) {
      try {
        (ctx.video as unknown as { src?: string }).src = freshSource.hlsUrl;
        (ctx.video as unknown as { load?: () => void }).load?.();
      } catch (e) {
        emitError(ctx, e);
      }
    } else {
      ctx.hls = await loadWithHls(ctx, ctx.video, freshSource.hlsUrl);

      // Add 401 error handler for future token expirations
      if (ctx.hls) {
        const hlsInstance = ctx.hls as unknown as {
          on?: (evt: string, cb: (evt: string, data: { response?: { code?: number } }) => void) => void;
        };
        if (typeof hlsInstance.on === "function") {
          hlsInstance.on("hlsError", (_evt: string, data: { response?: { code?: number } }) => {
            if (data.response?.code === 401) {
              // Token expired during playback - refresh immediately
              void refreshToken(ctx);
            }
          });
        }
      }
    }

    // Restore playback position once manifest is loaded
    const onCanPlay = () => {
      if (!ctx.video) return;
      ctx.video.currentTime = currentTime;
      if (!isPaused) {
        ctx.video.play().catch(() => {
          // Ignore autoplay failures
        });
      }
      ctx.video.removeEventListener("canplay", onCanPlay);
    };

    ctx.video.addEventListener("canplay", onCanPlay);
  } catch (error) {
    emitError(ctx, error);
  } finally {
    ctx.isRefreshing = false;
  }
}

/**
 * Start preemptive token refresh interval (90 minutes).
 * Refreshes before 2-hour token expiration to ensure seamless playback.
 */
export function startTokenRefreshInterval(ctx: ControllerContext): void {
  if (!ctx.titleId || !ctx.tokenRefreshHandler) {
    return;
  }

  // Clear existing interval if any
  if (ctx.tokenRefreshInterval) {
    clearInterval(ctx.tokenRefreshInterval);
  }

  // Refresh every 90 minutes (5400000ms) - safely before 2-hour expiration
  ctx.tokenRefreshInterval = setInterval(
    () => {
      void refreshToken(ctx);
    },
    90 * 60 * 1000,
  );
}

/**
 * Stop token refresh interval and clean up.
 */
export function stopTokenRefreshInterval(ctx: ControllerContext): void {
  if (ctx.tokenRefreshInterval) {
    clearInterval(ctx.tokenRefreshInterval);
    ctx.tokenRefreshInterval = null;
  }
}
